/**
 * Board — workflow-oriented kanban replacing the old Tasks page.
 *
 * Two views, toggled by a Segmented control bound to `?view=`:
 *   - Intakes  (default): one column per WorkflowStageId, cards = intakes.
 *   - Projects:           one column per status group, cards = projects.
 *
 * Drag a card across columns to advance its stage / change its status group.
 * Persistence happens through `useBoardData.moveItem`. Optimistic UI lives
 * inside the hook so this component stays presentational.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  App as AntApp,
  Card,
  Segmented,
  Space,
  Typography,
} from "antd";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { TableSkeleton } from "../components/TableSkeleton";
import { EmptyState } from "../components/EmptyState";
import { KanbanColumn } from "../components/board/KanbanColumn";
import { KanbanCard } from "../components/board/KanbanCard";
import {
  useBoardData,
  type BoardItem,
  type BoardView,
} from "../components/board/useBoardData";

const VIEW_VALUES: BoardView[] = ["intakes", "projects"];

function parseView(search: string): BoardView {
  const params = new URLSearchParams(search);
  const v = params.get("view");
  return VIEW_VALUES.includes(v as BoardView) ? (v as BoardView) : "intakes";
}

export default function Board() {
  const { message } = AntApp.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const view = parseView(location.search);

  const { items, columns, loading, moveItem } = useBoardData(view);

  // Sensors: pointer + keyboard so the board is operable without a mouse.
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const setView = useCallback(
    (next: BoardView) => {
      const params = new URLSearchParams(location.search);
      params.set("view", next);
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, {
        replace: true,
      });
    },
    [location.pathname, location.search, navigate]
  );

  // Initialize ?view= on first mount if missing so the URL always reflects state.
  useEffect(() => {
    if (!new URLSearchParams(location.search).has("view")) {
      setView("intakes");
    }
  }, [location.search, setView]);

  const itemsByColumn = useMemo(() => {
    const map: Record<string, BoardItem[]> = {};
    for (const col of columns) map[col.id] = [];
    for (const item of items) {
      if (!map[item.columnId]) map[item.columnId] = [];
      map[item.columnId].push(item);
    }
    return map;
  }, [items, columns]);

  const [busy, setBusy] = useState(false);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const sourceCol = (active.data.current as { columnId?: string } | undefined)
        ?.columnId;
      const targetCol = String(over.id);
      if (!sourceCol || sourceCol === targetCol) return;
      setBusy(true);
      try {
        await moveItem(String(active.id), targetCol);
      } catch {
        message.error("Could not move card. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [moveItem, message]
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={2} style={{ marginBottom: 0 }}>
          Board
        </Typography.Title>
        <Typography.Text type="secondary">
          Kanban view of intakes and projects. Drag a card to advance it.
        </Typography.Text>
      </div>

      <Card>
        <Segmented<BoardView>
          value={view}
          onChange={(value) => setView(value)}
          options={[
            { label: "Intakes", value: "intakes" },
            { label: "Projects", value: "projects" },
          ]}
          aria-label="Toggle between intakes and projects views"
        />
      </Card>

      {loading ? (
        <Card>
          <TableSkeleton rows={4} columns={4} />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            description={
              view === "intakes"
                ? "No intakes yet. Submissions from /agency and /vendor will appear here."
                : "No projects yet. Promote an intake to create one."
            }
          />
        </Card>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div
            role="region"
            aria-label={`${view === "intakes" ? "Intakes" : "Projects"} board`}
            aria-busy={busy}
            style={{
              display: "flex",
              gap: 16,
              overflowX: "auto",
              paddingBottom: 8,
              alignItems: "flex-start",
            }}
          >
            {columns.map((col) => (
              <KanbanColumn
                key={col.id}
                id={col.id}
                label={col.label}
                items={itemsByColumn[col.id] ?? []}
                renderCard={(item) => <KanbanCard item={item} />}
              />
            ))}
          </div>
        </DndContext>
      )}
    </Space>
  );
}
